import React from "react";
import { Link } from "react-router-dom";
import {
  Compass, Bike, Ship, Waves, Sailboat, UtensilsCrossed, Landmark,
  Users, Heart, MapPin, Plane, TreePalm, Mountain, Search, CalendarCheck,
  Sparkles, ArrowRight, Handshake, Globe2,
} from "lucide-react";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";

const HERO = "https://images.unsplash.com/photo-1747336755468-2c12efa216c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxDYXJpYmJlYW4lMjBpc2xhbmQlMjBhZXJpYWwlMjB0dXJxdW9pc2UlMjBjb2FzdHxlbnwwfHx8fDE3ODkzMzg3MDZ8MA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { icon: Bike, label: "ATV & off-road adventures" },
  { icon: Compass, label: "Island sightseeing tours" },
  { icon: Sailboat, label: "Boat tours & sailing" },
  { icon: Waves, label: "Snorkeling & water activities" },
  { icon: TreePalm, label: "Beach experiences" },
  { icon: Sparkles, label: "Private tours" },
  { icon: Plane, label: "Airport & transportation" },
  { icon: UtensilsCrossed, label: "Food & cultural tours" },
  { icon: Landmark, label: "Historical & heritage sites" },
  { icon: Users, label: "Family activities" },
  { icon: Heart, label: "Romantic experiences" },
  { icon: Ship, label: "Cruise shore excursions" },
  { icon: Mountain, label: "Adventure tours" },
  { icon: Globe2, label: "Nature & eco experiences" },
];

const DESTINATIONS = [
  {
    name: "Grenada",
    img: "https://images.unsplash.com/photo-1566159269137-ccf6e35a126e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxHcmVuYWRhJTIwQmFyYmFkb3MlMjBiZWFjaCUyMHRyb3BpY2FsfGVufDB8fHx8MTc4OTMzODcwNnww&ixlib=rb-4.1.0&q=85",
    copy: "Discover waterfalls and beaches, ride an ATV across the island, get out on the water or dive into local culture on the Spice Isle.",
  },
  {
    name: "St. Vincent & the Grenadines",
    img: "https://images.unsplash.com/photo-1636728163078-59ea0afb7665?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxHcmVuYWRhJTIwQmFyYmFkb3MlMjBiZWFjaCUyMHRyb3BpY2FsfGVufDB8fHx8MTc4OTMzODcwNnww&ixlib=rb-4.1.0&q=85",
    copy: "Find island adventures, marine experiences, sightseeing and locally operated tours across a chain of unspoiled islands.",
  },
  {
    name: "Barbados",
    img: "https://images.unsplash.com/photo-1636728163781-c8bc2d2d9730?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxHcmVuYWRhJTIwQmFyYmFkb3MlMjBiZWFjaCUyMHRyb3BpY2FsfGVufDB8fHx8MTc4OTMzODcwNnww&ixlib=rb-4.1.0&q=85",
    copy: "Discover activities, attractions and excursions that help you see far more than just your resort.",
  },
];

const STEPS = [
  { icon: Search, title: "Search", copy: "Explore Caribbean tours and experiences by island and category." },
  { icon: Compass, title: "Discover", copy: "Compare experiences and learn exactly what's included." },
  { icon: CalendarCheck, title: "Book", copy: "Reserve your spot conveniently in one place, before or during your trip." },
  { icon: Sparkles, title: "Explore", copy: "Show up and enjoy an unforgettable Caribbean adventure." },
];

export default function About() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Carivisor",
    description:
      "Carivisor is a Caribbean tours and experiences marketplace connecting travelers with excursions, activities and local tour operators across Grenada, Barbados, St. Vincent and the wider Caribbean.",
    url: typeof window !== "undefined" ? window.location.origin : "",
    slogan: "Discover. Book. Experience the Caribbean.",
    areaServed: ["Grenada", "Barbados", "St. Vincent and the Grenadines", "Caribbean"],
  };

  return (
    <div className="min-h-screen bg-[#060810]">
      <Seo
        title="About Carivisor | Caribbean Tours, Excursions & Experiences"
        description="Discover Carivisor, a Caribbean tours and experiences marketplace connecting travelers with exciting excursions, activities and local tour operators across Grenada, Barbados, St. Vincent and the Caribbean."
        keywords="Caribbean tours and experiences, Caribbean excursions, things to do in the Caribbean, Caribbean activities, Caribbean tour booking, Caribbean adventure tours, Caribbean shore excursions, Grenada tours and excursions, Barbados tours, St. Vincent tours"
        path="/about"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="Aerial view of a turquoise Caribbean coast" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060810]/70 via-[#060810]/80 to-[#060810]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-36 text-center on-dark">
          <p className="text-xs font-mono-caption uppercase tracking-[0.35em] text-cyan-400 mb-5">About Carivisor</p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Discover the Caribbean. <span className="text-gradient-warm">Experience More.</span>
          </h1>
          <p className="text-slate-300 text-base md:text-lg mt-6 max-w-3xl mx-auto leading-relaxed">
            Carivisor is a Caribbean tours, activities and experiences booking platform created to make discovering and
            booking unforgettable things to do across the Caribbean easier — connecting travelers with experiences from
            local tour operators throughout the islands.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/browse" data-testid="about-browse-cta" className="px-6 py-3 rounded-full btn-neon font-semibold flex items-center gap-2">
              Explore experiences <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/faq" data-testid="about-faq-cta" className="px-6 py-3 rounded-full glass text-slate-200 hover:text-cyan-400 font-semibold">
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Marketplace built for the Caribbean */}
        <section className="py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl leading-tight mb-5">
              A Caribbean travel marketplace <span className="text-gradient-warm">built for the Caribbean</span>
            </h2>
            <div className="space-y-4 text-slate-300 leading-relaxed text-base">
              <p>The Caribbean is more than beautiful beaches. Every island has its own culture, food, landscapes, communities and adventures waiting to be discovered — and Carivisor was created with that in mind.</p>
              <p>Our goal is to build a trusted marketplace where visitors can explore Caribbean tours and excursions while giving local tour operators and experience providers greater access to travelers from around the world.</p>
              <p>Instead of limiting your vacation to what you find after arriving at your hotel or cruise port, Carivisor helps you research and discover experiences before and during your trip.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="glass rounded-2xl p-5">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#ff5e00] to-[#d900ff] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-white text-lg">{s.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.copy}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 border-t border-[#1D2640]">
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl mb-3">Find things to do across the Caribbean</h2>
          <p className="text-slate-400 max-w-2xl mb-10">
            Whether you have a few hours ashore from a cruise ship or a whole week to explore, Carivisor makes it easier to
            find experiences that match the way you want to travel.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="about-categories">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="glass glass-hover rounded-xl p-4 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-cyan-300 shrink-0" />
                  <span className="text-sm text-slate-200">{c.label}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Destinations */}
        <section className="py-16 border-t border-[#1D2640]">
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl mb-3">Explore Grenada and beyond</h2>
          <p className="text-slate-400 max-w-2xl mb-10">
            Carivisor's Caribbean journey begins with Grenada, St. Vincent and the Grenadines, and Barbados — with a vision
            of connecting travelers with experiences throughout the wider Caribbean.
          </p>
          <div className="grid md:grid-cols-3 gap-6" data-testid="about-destinations">
            {DESTINATIONS.map((d) => (
              <div key={d.name} className="group glass glass-hover rounded-2xl overflow-hidden flex flex-col">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={d.img} alt={`Things to do in ${d.name}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-transparent to-transparent opacity-70" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white">
                    <MapPin className="w-4 h-4 text-orange-400" />
                    <span className="font-heading font-bold">{d.name}</span>
                  </div>
                </div>
                <div className="p-5 flex-1">
                  <p className="text-sm text-slate-400 leading-relaxed">{d.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Supporting operators */}
        <section className="py-16 border-t border-[#1D2640] grid md:grid-cols-2 gap-12 items-center">
          <div className="glass rounded-2xl p-8 order-2 md:order-1">
            <Handshake className="w-10 h-10 text-emerald-400 mb-4" />
            <h3 className="font-heading font-bold text-white text-2xl mb-3">Why travelers choose Carivisor</h3>
            <p className="text-slate-400 leading-relaxed text-sm">
              Finding great Caribbean experiences shouldn't be complicated. Discover activities by destination, compare
              experiences, learn what's included, and choose adventures that fit your vacation. Because our focus is
              specifically on the Caribbean, we highlight the experiences that make each destination unique — from famous
              attractions to local adventures you might otherwise never discover.
            </p>
          </div>
          <div className="order-1 md:order-2">
            <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl leading-tight mb-5">
              Supporting local <span className="text-gradient-warm">Caribbean tour operators</span>
            </h2>
            <div className="space-y-4 text-slate-300 leading-relaxed text-base">
              <p>Carivisor isn't only built for travelers — it's built for the people who make Caribbean tourism possible.</p>
              <p>Local guides, excursion companies, attractions, transportation providers and independent tour operators deserve the opportunity to reach travelers around the world. Carivisor gives participating operators a marketplace to showcase their experiences and connect with visitors actively searching for things to do.</p>
              <p>When travelers explore the Caribbean through local experiences, more of the tourism economy reaches the businesses and communities that create them.</p>
            </div>
            <Link to="/register" className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full glass text-cyan-300 hover:text-white font-semibold">
              Become a partner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 border-t border-[#1D2640] text-center">
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-5xl leading-tight mb-5">
            Your Caribbean adventure <span className="text-gradient-warm">starts here</span>
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Ride an ATV along an island trail. Sail across turquoise water. Explore a waterfall. Discover local food. Visit
            historic communities. Spend the day on the sea. Or simply find something you've never done before.
          </p>
          <p className="font-mono-caption text-xs uppercase tracking-[0.3em] text-cyan-400 mt-8">Carivisor · Discover. Book. Experience the Caribbean.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/browse" className="px-7 py-3 rounded-full btn-neon font-semibold flex items-center gap-2">
              Start exploring <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
